/**
 * decideScroll — pure decision logic for the chat scroll-to-bottom effect.
 *
 * Extracted out of App.tsx (which isn't unit-tested — it's excluded from
 * coverage per this project's convention) so this logic has real test
 * coverage. Two real bugs shipped in this function's inline form before it
 * was extracted (see startup-sequencing spec's Decisions section) — both
 * were edge cases in the tracker-reset/zero-message interaction that a
 * quick read-through of the diff didn't catch. Pure functions with unit
 * tests catch exactly this class of bug.
 */

export interface ScrollTrackerState {
  lastScrolledConversationId: string | null;
  lastScrolledMessageCount: number;
}

export interface ScrollDecision {
  /** Whether to call scrollIntoView at all. */
  shouldScroll: boolean;
  /** Only meaningful when shouldScroll is true. */
  behavior: 'auto' | 'smooth';
  /** The tracker state to persist for the next call. */
  nextState: ScrollTrackerState;
}

/**
 * Decide whether the current render should scroll the chat to the bottom,
 * and with what behavior — instant on first landing (boot ready, or
 * switching to a different conversation), smooth for a genuinely new
 * message arriving in the same conversation. Never scrolls again for a
 * same-length re-render (e.g. metadata patching in names/hidden-turns), and
 * never scrolls when there's nothing to scroll to (an empty conversation).
 */
export function decideScroll(
  activeConversationId: string | null,
  messageCount: number,
  tracker: ScrollTrackerState,
): ScrollDecision {
  const isNewConversation = activeConversationId !== tracker.lastScrolledConversationId;
  const lastCount = isNewConversation ? 0 : tracker.lastScrolledMessageCount;

  if (messageCount === 0) {
    // Nothing to scroll to. Persist the conversation switch (if any) so a
    // later message in this same conversation is correctly treated as
    // "first message" rather than compared against a stale prior count —
    // but do NOT persist messageCount here, since count is already 0.
    return {
      shouldScroll: false,
      behavior: 'auto',
      nextState: { lastScrolledConversationId: activeConversationId, lastScrolledMessageCount: 0 },
    };
  }

  const isFirstScrollForThisLanding = lastCount === 0;
  const isNewMessage = messageCount > lastCount;
  const shouldScroll = isFirstScrollForThisLanding || isNewMessage;

  return {
    shouldScroll,
    behavior: isFirstScrollForThisLanding ? 'auto' : 'smooth',
    nextState: { lastScrolledConversationId: activeConversationId, lastScrolledMessageCount: messageCount },
  };
}
